import React, { useMemo, useState } from "react";
import {
    Box, IconButton, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Tabs, Tab, TextField,
    Tooltip
} from "@mui/material";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SortIcon from '@mui/icons-material/Sort';
import HistoryIcon from '@mui/icons-material/History';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { Currency, WalletTransactions } from "../interfaces/ProducerDashboard";
import { formatDate, formatMoney } from "./ProducerDashboardHelper";

type TransactionsProps = {
    walletTransactions: WalletTransactions,
    currency: Currency
    walletId: string
}

export default function Transactions({ walletTransactions, currency, walletId }: TransactionsProps) {
    const [tabIndex, setTabIndex] = useState(0);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [transactionSearch, setTransactionSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const getProcessedTransactions = useMemo(() => {
        const wt = walletTransactions;
        let txs: any[] = [];
        if (tabIndex === 0) txs = [...(wt.transactions_out || [])];
        else if (tabIndex === 1) txs = [...(wt.transactions_in || [])];
        else if (tabIndex === 2) txs = [...(wt.transactions_p2_p || [])];

        if (dateFrom || dateTo) {
            txs = txs.filter(tx => {
                const txDate = tx.date * 1000;
                const from = dateFrom ? new Date(dateFrom).getTime() : 0;
                const to = dateTo ? new Date(dateTo).setHours(23, 59, 59, 999) : Infinity;
                return txDate >= from && txDate <= to;
            });
        }
        if (transactionSearch) {
            const query = transactionSearch.toLowerCase();
            txs = txs.filter(tx =>
                (tx.id?.toString().includes(query)) ||
                (tx.comment?.toLowerCase().includes(query))
            );
        }
        return txs.sort((a, b) => sortOrder === 'asc' ? a.date - b.date : b.date - a.date);
    }, [walletTransactions, tabIndex, sortOrder, transactionSearch, dateFrom, dateTo]);

    return (
        <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <HistoryIcon fontSize="small" color="primary" /> Wallet Transactions
            </Typography>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
                        <Tabs
                            value={tabIndex}
                            onChange={(_, v) => setTabIndex(v)}
                            sx={{ minHeight: 40, '& .MuiTab-root': { fontSize: '0.7rem', fontWeight: 700, minHeight: 40 } }}
                        >
                            <Tab label="MONEY OUT" icon={<ArrowUpwardIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                            <Tab label="MONEY IN" icon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                            <Tab label="P2P" icon={<SwapHorizIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                        </Tabs>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 1 }}>
                            <TextField type="date" size="small" value={dateFrom} onChange={e => setDateFrom(e.target.value)} sx={{ width: 130, '& .MuiInputBase-input': { fontSize: '0.75rem' }, bgcolor: 'white' }} />
                            <TextField type="date" size="small" value={dateTo} onChange={e => setDateTo(e.target.value)} sx={{ width: 130, '& .MuiInputBase-input': { fontSize: '0.75rem' }, bgcolor: 'white' }} />
                            <TextField size="small" placeholder="Search..." value={transactionSearch} onChange={e => setTransactionSearch(e.target.value)} sx={{ width: 150, '& .MuiInputBase-input': { fontSize: '0.75rem' }, bgcolor: 'white' }} />
                            <IconButton size="small" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
                                <SortIcon sx={{ fontSize: 20, transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Box>

                <TableContainer sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ "& th": { bgcolor: '#f8f9fa', fontWeight: 800, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' } }}>
                                <TableCell>ID</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Comment</TableCell>
                                <Tooltip title='S -> sender - R -> receiver'>
                                    <TableCell>Account Flow</TableCell>
                                </Tooltip>
                                <TableCell align="center">Debit / Credit</TableCell>
                                <TableCell align="right">Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getProcessedTransactions.map((tx: any) => {
                                const amountVal = (tx.credit_amount || tx.debit_amount || 0) / 100;
                                const isDebit = tx.sender_account_id === walletId;
                                return (
                                    <TableRow key={tx.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{tx.id}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(tx.date)}</TableCell>
                                        <TableCell>
                                            <Tooltip title={tx.comment}>
                                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {tx.comment || '-'}
                                                </Typography>
                                            </Tooltip>

                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}>
                                            <Box><span style={{ fontWeight: 600 }}>S:</span> <span style={{ color: isDebit ? '#8d0e1d' : '#013f75' }}>{tx.sender_account_id || '-'}</span></Box>
                                            <Box><span style={{ fontWeight: 600 }}>R:</span> <span style={{ color: !isDebit ? '#083602' : '#013f75' }}> {tx.receiver_account_id || '-'}</span></Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            {isDebit
                                                ? <TrendingDownIcon sx={{ color: 'error.main' }} />
                                                : <TrendingUpIcon sx={{ color: 'success.main' }} />
                                            }
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography sx={{
                                                fontWeight: 800,
                                                fontSize: '0.8rem',
                                                color: isDebit ? 'error.main' : (!isDebit && amountVal > 0 ? 'success.main' : 'text.primary')
                                            }}>
                                                {isDebit ? '-' : (tabIndex === 1 ? '+' : '')}{formatMoney(amountVal, currency)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {getProcessedTransactions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8rem' }}>
                                        No transactions found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}